import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, useWindowDimensions } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import BottomSheet from '../components/ui/BottomSheet';
import { Calendar, CheckCircle2, ArrowRight, X, AlertTriangle, Clock, Wrench, Settings, User, Briefcase, Plus, Edit, Trash2 } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { formatDateOnly, formatDateForDisplay } from '../lib/dateUtils';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
}

interface HomeSystem {
  id: string;
  home_id: string;
  heating_systems: string[];
  cooling_systems: string[];
  appliances: string[];
  other_systems: string[];
  upgrades: string[];
  outdoor_features: string[];
  maintenance_plan_enabled?: {
    heating_systems?: string[];
    cooling_systems?: string[];
    appliances?: string[];
    other_systems?: string[];
    upgrades?: string[];
    outdoor_features?: string[];
  };
}

interface MaintenanceTemplate {
  id: string;
  system_type: string;
  system_name: string;
  task_name: string;
  description: string;
  frequency_months: number;
  estimated_duration_minutes: number;
  priority: string;
  professional_required: boolean;
}

interface MaintenancePlan {
  id: string;
  home_id: string;
  task_id: string;
  next_due_date: string;
  last_completed_date: string | null;
  status: string;
  completed_by: 'homeowner' | 'professional' | null;
  completion_notes: string | null;
  service_provider_id: string | null;
  is_custom?: boolean;
  custom_task_name?: string;
  custom_description?: string;
  custom_frequency_months?: number;
  inventory_item_id?: string | null;
  maintenance_tasks: {
    id: string;
    name: string;
    description: string;
    frequency_months: number;
    estimated_duration_minutes: number;
  };
  maintenance_templates?: {
    system_type: string;
    system_name: string;
    priority: string;
    professional_required: boolean;
  };
}

const systemOptions = {
  heating_systems: [
    { id: 'furnace', label: 'Furnace' },
    { id: 'heat_pump', label: 'Heat Pump' },
    { id: 'boiler', label: 'Boiler' },
    { id: 'radiant', label: 'Radiant Heat' },
    { id: 'baseboard', label: 'Electric Baseboard' },
    { id: 'fireplace', label: 'Fireplace' }
  ],
  cooling_systems: [
    { id: 'central_ac', label: 'Central AC' },
    { id: 'mini_split', label: 'Mini Split' },
    { id: 'window_units', label: 'Window Units' },
    { id: 'swamp_cooler', label: 'Swamp Cooler' }
  ],
  appliances: [
    { id: 'washer', label: 'Washer' },
    { id: 'dryer', label: 'Dryer' },
    { id: 'dishwasher', label: 'Dishwasher' },
    { id: 'refrigerator', label: 'Refrigerator' },
    { id: 'oven', label: 'Oven/Range' },
    { id: 'microwave', label: 'Built-in Microwave' },
    { id: 'garbage_disposal', label: 'Garbage Disposal' }
  ],
  other_systems: [
    { id: 'generator', label: 'Generator' },
    { id: 'water_softener', label: 'Water Softener' },
    { id: 'water_filter', label: 'Water Filtration System' },
    { id: 'sump_pump', label: 'Sump Pump' },
    { id: 'security_system', label: 'Security System' }
  ],
  upgrades: [
    { id: 'solar_panels', label: 'Solar Panels' },
    { id: 'ev_charger', label: 'EV Charger' },
    { id: 'electronic_locks', label: 'Electronic Locks' },
    { id: 'smart_thermostat', label: 'Smart Thermostat' },
    { id: 'whole_house_fan', label: 'Whole House Fan' }
  ],
  outdoor_features: [
    { id: 'gutters', label: 'Gutters' },
    { id: 'pool', label: 'Pool' },
    { id: 'hot_tub', label: 'Hot Tub' },
    { id: 'sprinklers', label: 'Sprinkler System' },
    { id: 'deck', label: 'Deck' },
    { id: 'patio', label: 'Patio' },
    { id: 'fence', label: 'Fence' }
  ]
};

// Custom Dropdown Picker Component
const DropdownPicker = ({ 
  visible, 
  onClose, 
  options, 
  selectedValue, 
  onSelect 
}: { 
  visible: boolean; 
  onClose: () => void; 
  options: Array<{ value: string; label: string }>; 
  selectedValue: string; 
  onSelect: (value: string) => void;
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={pickerStyles.pickerModalOverlay}>
        <Pressable style={pickerStyles.pickerModalBackdrop} onPress={onClose} />
        <View style={pickerStyles.pickerModalContent}>
          <View style={pickerStyles.pickerHeader}>
            <Text style={pickerStyles.pickerHeaderText}>Select Home</Text>
            <Pressable onPress={onClose}>
              <X size={20} color="#6b7280" />
            </Pressable>
          </View>
          <WebScrollView style={pickerStyles.pickerOptions}>
            {options.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => {
                  onSelect(option.value);
                  onClose();
                }}
                style={[
                  pickerStyles.pickerOption,
                  selectedValue === option.value && pickerStyles.pickerOptionSelected
                ]}
              >
                <Text style={[
                  pickerStyles.pickerOptionText,
                  selectedValue === option.value && pickerStyles.pickerOptionTextSelected
                ]}>
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

const COMPACT_HOME_SELECTOR_BREAKPOINT = 768;

const MaintenancePlan = () => {
  const { width: windowWidth } = useWindowDimensions();
  const isCompactHomeSelector = windowWidth < COMPACT_HOME_SELECTOR_BREAKPOINT;
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const [homes, setHomes] = useState<Home[]>([]);
  const [selectedHome, setSelectedHome] = useState<string>('');
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [systemData, setSystemData] = useState<Partial<HomeSystem>>({
    heating_systems: [],
    cooling_systems: [],
    appliances: [],
    other_systems: [],
    upgrades: [],
    outdoor_features: []
  });
  const [maintenancePlanEnabled, setMaintenancePlanEnabled] = useState<{
    heating_systems: string[];
    cooling_systems: string[];
    appliances: string[];
    other_systems: string[];
    upgrades: string[];
    outdoor_features: string[];
  }>({
    heating_systems: [],
    cooling_systems: [],
    appliances: [],
    other_systems: [],
    upgrades: [],
    outdoor_features: []
  });
  const [maintenancePlans, setMaintenancePlans] = useState<MaintenancePlan[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateSuccess, setRegenerateSuccess] = useState(false);
  const [showHomePicker, setShowHomePicker] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<MaintenancePlan | null>(null);
  const [completionData, setCompletionData] = useState({
    completion_date: new Date().toISOString().split('T')[0],
    performed_by: 'homeowner' as 'homeowner' | 'professional',
    notes: ''
  });
  const [completing, setCompleting] = useState(false);
  const [showCustomTaskModal, setShowCustomTaskModal] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<Array<{id: string; name: string; category: string}>>([]);
  const [customTaskData, setCustomTaskData] = useState({
    task_name: '',
    description: '',
    frequency_months: '12',
    inventory_item_id: '',
    first_due_date: formatDateOnly(new Date())
  });
  const [creatingCustomTask, setCreatingCustomTask] = useState(false);
  const [showEditFrequencyModal, setShowEditFrequencyModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<MaintenancePlan | null>(null);
  const [editFrequencyData, setEditFrequencyData] = useState({
    frequency_months: ''
  });
  const [updatingFrequency, setUpdatingFrequency] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [snoozingPlan, setSnoozingPlan] = useState<MaintenancePlan | null>(null);
  const [snoozeData, setSnoozeData] = useState({
    months: '3'
  });
  const [snoozing, setSnoozing] = useState(false);
  const reviewStepInitialized = useRef(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<MaintenancePlan | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setHomes(accessibleHomes as Home[]);
    if (activeHome?.id) {
      setSelectedHome(activeHome.id);
    } else if (!homesLoading) {
      setSelectedHome('');
    }
    // Must clear loading whenever context finishes, including when activeHome is set (multi-home).
    if (!homesLoading) {
      setLoading(false);
    }
  }, [accessibleHomes, activeHome, homesLoading]);

  const fetchHomeSystem = async (homeId: string) => {
    try {
      const { data, error } = await supabase
        .from('home_systems')
        .select('*')
        .eq('home_id', homeId);
      
      if (error) throw error;
      
      // Handle the case where no data exists
      if (data && data.length > 0) {
        const system = data[0];
        setSystemData({
          heating_systems: system.heating_systems || [],
          cooling_systems: system.cooling_systems || [],
          appliances: system.appliances || [],
          other_systems: system.other_systems || [],
          upgrades: system.upgrades || [],
          outdoor_features: system.outdoor_features || []
        });
        
        // Load maintenance plan preferences, defaulting to empty arrays
        const enabled = system.maintenance_plan_enabled || {};
        setMaintenancePlanEnabled({
          heating_systems: enabled.heating_systems || [],
          cooling_systems: enabled.cooling_systems || [],
          appliances: enabled.appliances || [],
          other_systems: enabled.other_systems || [],
          upgrades: enabled.upgrades || [],
          outdoor_features: enabled.outdoor_features || []
        });
      } else {
        // Reset to empty arrays if no data exists
        setSystemData({
          heating_systems: [],
          cooling_systems: [],
          appliances: [],
          other_systems: [],
          upgrades: [],
          outdoor_features: []
        });
        setMaintenancePlanEnabled({
          heating_systems: [],
          cooling_systems: [],
          appliances: [],
          other_systems: [],
          upgrades: [],
          outdoor_features: []
        });
      }
    } catch (error) {
      console.error('Error fetching home system:', error);
      // Reset to empty arrays on error
      setSystemData({
        heating_systems: [],
        cooling_systems: [],
        appliances: [],
        other_systems: [],
        upgrades: [],
        outdoor_features: []
      });
      setMaintenancePlanEnabled({
        heating_systems: [],
        cooling_systems: [],
        appliances: [],
        other_systems: [],
        upgrades: [],
        outdoor_features: []
      });
    }
  };

  const fetchMaintenancePlans = async () => {
    if (!selectedHome) return;
    
    setTasksLoading(true);
    try {
      const { data, error } = await supabase
        .from('maintenance_plans')
        .select(`
          *,
          maintenance_tasks (
            id,
            name,
            description,
            frequency_months,
            estimated_duration_minutes
          )
        `)
        .eq('home_id', selectedHome)
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      
      // Get system info from maintenance_templates by matching task IDs
      if (data && data.length > 0) {
        const taskIds = data.map(plan => plan.task_id).filter(Boolean);
        
        if (taskIds.length > 0) {
          const { data: templates, error: templatesError } = await supabase
            .from('maintenance_templates')
            .select('id, system_type, system_name, priority, professional_required')
            .in('id', taskIds);
          
          if (!templatesError && templates) {
            // Map templates to plans
            const templatesMap = templates.reduce((acc, template) => {
              acc[template.id] = template;
              return acc;
            }, {} as Record<string, any>);
            
            const plansWithTemplates = data.map(plan => ({
              ...plan,
              maintenance_templates: templatesMap[plan.task_id] || null
            }));
            
            setMaintenancePlans(plansWithTemplates);
        return;
      }
        }
      }
      
      setMaintenancePlans(data || []);
    } catch (error) {
      console.error('Error fetching maintenance plans:', error);
      setMaintenancePlans([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    if (!selectedHome) return;
    
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, name, category')
        .eq('home_id', selectedHome)
        .order('name');

      if (error) throw error;
      setInventoryItems(data || []);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setInventoryItems([]);
    }
  };

  const handleOpenCustomTaskModal = () => {
    setCustomTaskData({
      task_name: '',
      description: '',
      frequency_months: '12',
      inventory_item_id: '',
      first_due_date: formatDateOnly(new Date())
    });
    fetchInventoryItems();
    setShowCustomTaskModal(true);
  };

  const handleCloseCustomTaskModal = () => {
    setShowCustomTaskModal(false);
    setCustomTaskData({
      task_name: '',
      description: '',
      frequency_months: '12',
      inventory_item_id: '',
      first_due_date: formatDateOnly(new Date())
    });
  };

  const handleCreateCustomTask = async () => {
    if (!selectedHome || !customTaskData.task_name.trim()) {
      setError('Please provide a task name');
      return;
    }

    const frequencyMonths = parseInt(customTaskData.frequency_months);
    if (isNaN(frequencyMonths) || frequencyMonths < 1) {
      setError('Please provide a valid frequency (at least 1 month)');
      return;
    }

    setCreatingCustomTask(true);
    setError('');

    try {
      const { data, error } = await supabase.rpc('create_custom_maintenance_task', {
        p_home_id: selectedHome,
        p_task_name: customTaskData.task_name.trim(),
        p_description: customTaskData.description.trim() || null,
        p_frequency_months: frequencyMonths,
        p_inventory_item_id: customTaskData.inventory_item_id || null,
        p_first_due_date: customTaskData.first_due_date || null
      });

      if (error) throw error;

      // Refresh maintenance plans
      await fetchMaintenancePlans();
      handleCloseCustomTaskModal();
    } catch (error: any) {
      console.error('Error creating custom task:', error);
      setError(error.message || 'Failed to create custom task. Please try again.');
    } finally {
      setCreatingCustomTask(false);
    }
  };

  const handleOpenEditFrequencyModal = (plan: MaintenancePlan) => {
    setEditingPlan(plan);
    // Get current frequency - use custom_frequency_months if set, otherwise use template frequency
    const currentFrequency = plan.is_custom 
      ? plan.custom_frequency_months 
      : (plan.custom_frequency_months || plan.maintenance_tasks?.frequency_months);
    setEditFrequencyData({
      frequency_months: currentFrequency?.toString() || '12'
    });
    setShowEditFrequencyModal(true);
  };

  const handleCloseEditFrequencyModal = () => {
    setShowEditFrequencyModal(false);
    setEditingPlan(null);
    setEditFrequencyData({ frequency_months: '' });
  };

  const handleUpdateFrequency = async () => {
    if (!editingPlan) return;

    const frequencyMonths = parseInt(editFrequencyData.frequency_months);
    if (isNaN(frequencyMonths) || frequencyMonths < 1) {
      setError('Please provide a valid frequency (at least 1 month)');
      return;
    }

    setUpdatingFrequency(true);
    setError('');

    try {
      // Update the custom_frequency_months field (works for both custom and template tasks)
      const { error } = await supabase
        .from('maintenance_plans')
        .update({ 
          custom_frequency_months: frequencyMonths,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPlan.id);

      if (error) throw error;

      // Refresh maintenance plans
      await fetchMaintenancePlans();
      handleCloseEditFrequencyModal();
    } catch (error: any) {
      console.error('Error updating frequency:', error);
      setError(error.message || 'Failed to update frequency. Please try again.');
    } finally {
      setUpdatingFrequency(false);
    }
  };

  const handleOpenSnoozeModal = (plan: MaintenancePlan) => {
    setSnoozingPlan(plan);
    setSnoozeData({ months: '3' });
    setShowSnoozeModal(true);
  };

  const handleCloseSnoozeModal = () => {
    setShowSnoozeModal(false);
    setSnoozingPlan(null);
    setSnoozeData({ months: '3' });
  };

  const handleSnoozeTask = async () => {
    if (!snoozingPlan) return;

    const snoozeMonths = parseInt(snoozeData.months);
    if (isNaN(snoozeMonths) || snoozeMonths < 1) {
      setError('Please provide a valid number of months (at least 1)');
      return;
    }

    setSnoozing(true);
    setError('');

    try {
      // Calculate new next_due_date by adding snooze months to current next_due_date
      const currentDueDate = snoozingPlan.next_due_date 
        ? new Date(snoozingPlan.next_due_date)
        : new Date();
      
      const newDueDate = new Date(currentDueDate);
      newDueDate.setMonth(newDueDate.getMonth() + snoozeMonths);

      const { error } = await supabase
        .from('maintenance_plans')
        .update({ 
          next_due_date: newDueDate.toISOString().split('T')[0],
          updated_at: new Date().toISOString()
        })
        .eq('id', snoozingPlan.id);

      if (error) throw error;

      // Refresh maintenance plans
      await fetchMaintenancePlans();
      handleCloseSnoozeModal();
    } catch (error: any) {
      console.error('Error snoozing task:', error);
      setError(error.message || 'Failed to snooze task. Please try again.');
    } finally {
      setSnoozing(false);
    }
  };

  const handleOpenDeleteModal = (plan: MaintenancePlan) => {
    setDeletingPlan(plan);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingPlan(null);
  };

  const handleDeleteTask = async () => {
    if (!deletingPlan) return;

    setDeleting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('maintenance_plans')
        .delete()
        .eq('id', deletingPlan.id);

      if (error) throw error;

      // Refresh maintenance plans
      await fetchMaintenancePlans();
      handleCloseDeleteModal();
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message || 'Failed to delete task. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (selectedHome) {
      fetchHomeSystem(selectedHome);
      fetchMaintenancePlans();
    }
  }, [selectedHome]);

  const handleHomeChange = (homeId: string) => {
    setSelectedHome(homeId);
    setActiveHomeById(homeId);
    fetchHomeSystem(homeId);
  };

  const handleSystemToggle = (type: keyof typeof systemOptions, id: string) => {
    setSystemData(prev => ({
      ...prev,
      [type]: prev[type]?.includes(id)
        ? prev[type]?.filter(item => item !== id)
        : [...(prev[type] || []), id]
    }));
  };

  const handleSubmit = async () => {
    if (!selectedHome) return;
    setSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('home_systems')
        .upsert({
          home_id: selectedHome,
          ...systemData,
          maintenance_plan_enabled: maintenancePlanEnabled
        }, {
          onConflict: 'home_id'
        });

      if (error) throw error;
      
      // Generate maintenance plans for enabled systems
      const { error: generateError } = await supabase.rpc('generate_maintenance_plans_for_home', {
        home_id_param: selectedHome
      });
      
      if (generateError) {
        console.error('Error generating maintenance plans:', generateError);
        // Don't throw - systems are saved, plans can be generated later
      }
      
      setIsSetupOpen(false);
      setCurrentStep(0);
      fetchHomeSystem(selectedHome);
      fetchMaintenancePlans();
    } catch (error: any) {
      console.error('Error saving home systems:', error);
      setError('Failed to save home systems. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerateMaintenancePlans = async () => {
    if (!selectedHome) return;
    setRegenerating(true);
    setRegenerateSuccess(false);
    setError('');

    try {
      const { error } = await supabase.rpc('regenerate_maintenance_plans', {
        home_id_param: selectedHome
      });

      if (error) throw error;
      setRegenerateSuccess(true);
      
      // Refresh the plans
      fetchMaintenancePlans();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setRegenerateSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error('Error regenerating maintenance plans:', error);
      setError('Failed to regenerate maintenance plans. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleOpenCompleteModal = (plan: MaintenancePlan) => {
    setSelectedPlan(plan);
    setCompletionData({
      completion_date: new Date().toISOString().split('T')[0],
      performed_by: 'homeowner',
      notes: ''
    });
    setShowCompleteModal(true);
  };

  const handleCloseCompleteModal = () => {
    setShowCompleteModal(false);
    setSelectedPlan(null);
    setCompletionData({
      completion_date: new Date().toISOString().split('T')[0],
      performed_by: 'homeowner',
      notes: ''
    });
  };

  const handleCompleteTask = async () => {
    if (!selectedPlan) return;
    
    setCompleting(true);
    setError('');

    try {
      const { error } = await supabase.rpc('complete_maintenance_task', {
        plan_id: selectedPlan.id,
        completion_date: completionData.completion_date,
        performed_by: completionData.performed_by,
        notes: completionData.notes || null,
        provider_id: null
      });

      if (error) throw error;
      
      // Refresh the plans
      fetchMaintenancePlans();
      handleCloseCompleteModal();
    } catch (error: any) {
      console.error('Error completing task:', error);
      setError('Failed to mark task as complete. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'normal': return { color: '#2563eb', backgroundColor: '#eff6ff' };
      case 'low': return { color: '#16a34a', backgroundColor: '#f0fdf4' };
      default: return { color: '#4b5563', backgroundColor: '#f9fafb' };
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 
      ? `${hours} hr ${remainingMinutes} min`
      : `${hours} hr`;
  };

  // Initialize maintenance plan preferences when entering review step
  useEffect(() => {
    const totalSteps = Object.keys(systemOptions).length;
    if (currentStep === totalSteps && !reviewStepInitialized.current) {
      const hasAnySystems = Object.values(systemData).some(systems => 
        Array.isArray(systems) && systems.length > 0
      );
      // Initialize to all configured systems enabled by default
      if (hasAnySystems && Object.values(maintenancePlanEnabled).every(arr => arr.length === 0)) {
        setMaintenancePlanEnabled({
          heating_systems: systemData.heating_systems || [],
          cooling_systems: systemData.cooling_systems || [],
          appliances: systemData.appliances || [],
          other_systems: systemData.other_systems || [],
          upgrades: systemData.upgrades || [],
          outdoor_features: systemData.outdoor_features || []
        });
      }
      reviewStepInitialized.current = true;
    } else if (currentStep !== totalSteps) {
      reviewStepInitialized.current = false;
    }
  }, [currentStep, systemData, maintenancePlanEnabled]);

  const handleMaintenancePlanToggle = (category: keyof typeof systemOptions, systemId: string) => {
    setMaintenancePlanEnabled(prev => {
      const categoryEnabled = prev[category] || [];
      const isEnabled = categoryEnabled.includes(systemId);
      
      return {
        ...prev,
        [category]: isEnabled
          ? categoryEnabled.filter(id => id !== systemId)
          : [...categoryEnabled, systemId]
      };
    });
  };

  const renderSetupStep = () => {
    const steps = [
      { type: 'heating_systems', title: 'Heating', description: 'How is your home heated?' },
      { type: 'cooling_systems', title: 'Cooling', description: 'How is your home cooled?' },
      { type: 'appliances', title: 'Appliances', description: 'Which major appliances do you have?' },
      { type: 'other_systems', title: 'Other Systems', description: 'Do you have any of these systems?' },
      { type: 'upgrades', title: 'Upgrades', description: 'Have you installed any of these upgrades?' },
      { type: 'outdoor_features', title: 'Outdoor Features', description: 'What features do you have outside?' }
    ];

    // Review step is the last step
    if (currentStep === steps.length) {
      return renderReviewStep();
    }

    const currentStepData = steps[currentStep];
    const options = systemOptions[currentStepData.type as keyof typeof systemOptions];
    const selectedItems = systemData[currentStepData.type as keyof typeof systemOptions] || [];

    return (
      <View style={styles.setupStepContainer}>
        <View style={styles.setupStepHeader}>
          <Text style={styles.setupStepTitle}>{currentStepData.title}</Text>
          <Text style={styles.setupStepDescription}>{currentStepData.description}</Text>
        </View>

        <View style={styles.systemOptionsGrid}>
          {options.map(option => (
            <Pressable
              key={option.id}
              onPress={() => handleSystemToggle(currentStepData.type as keyof typeof systemOptions, option.id)}
              style={[
                styles.systemOption,
                selectedItems.includes(option.id) && styles.systemOptionSelected
              ]}
            >
              <Text style={[
                styles.systemOptionText,
                selectedItems.includes(option.id) && styles.systemOptionTextSelected
              ]}>
                {option.label}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    );
  };

  const renderReviewStep = () => {
    const getSystemLabel = (category: keyof typeof systemOptions, systemId: string): string => {
      const options = systemOptions[category];
      return options.find(opt => opt.id === systemId)?.label || systemId;
    };

    const hasAnySystems = Object.values(systemData).some(systems => 
      Array.isArray(systems) && systems.length > 0
    );

    if (!hasAnySystems) {
      return (
        <View style={styles.setupStepContainer}>
          <View style={styles.setupStepHeader}>
            <Text style={styles.setupStepTitle}>Review Configuration</Text>
            <Text style={styles.setupStepDescription}>
              You haven't configured any systems yet. Go back to add systems to your home.
            </Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.setupStepContainer}>
        <View style={styles.setupStepHeader}>
          <Text style={styles.setupStepTitle}>Review & Enable Maintenance Plans</Text>
          <Text style={styles.setupStepDescription}>
            Review your configured systems and toggle which ones should have maintenance plans generated.
          </Text>
        </View>

        <WebScrollView style={styles.reviewScrollView}>
          {Object.keys(systemOptions).map((categoryKey) => {
            const category = categoryKey as keyof typeof systemOptions;
            const configuredSystems = systemData[category] || [];
            
            if (configuredSystems.length === 0) return null;

            const categoryLabels: Record<string, string> = {
              heating_systems: 'Heating Systems',
              cooling_systems: 'Cooling Systems',
              appliances: 'Appliances',
              other_systems: 'Other Systems',
              upgrades: 'Upgrades',
              outdoor_features: 'Outdoor Features'
            };

            return (
              <View key={category} style={styles.reviewCategory}>
                <Text style={styles.reviewCategoryTitle}>{categoryLabels[category]}</Text>
                {configuredSystems.map((systemId) => {
                  const isEnabled = (maintenancePlanEnabled[category] || []).includes(systemId);
                  return (
                    <View key={systemId} style={styles.reviewSystemRow}>
                      <Text style={styles.reviewSystemLabel}>
                        {getSystemLabel(category, systemId)}
                      </Text>
                      <Pressable
                        onPress={() => handleMaintenancePlanToggle(category, systemId)}
                        style={[
                          styles.toggle,
                          isEnabled && styles.toggleEnabled
                        ]}
                      >
                        <View style={[
                          styles.toggleThumb,
                          isEnabled && styles.toggleThumbEnabled
                        ]} />
                      </Pressable>
                    </View>
                  );
                })}
              </View>
            );
          })}
        </WebScrollView>
      </View>
    );
  };

  const getTaskStatus = (plan: MaintenancePlan) => {
    if (!plan.next_due_date) return { text: 'Not scheduled', color: '#6b7280' };
    
    const dueDate = new Date(plan.next_due_date);
    const today = new Date();
    const daysUntilDue = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) {
      return { text: `Overdue by ${Math.abs(daysUntilDue)} days`, color: '#dc2626' };
    } else if (daysUntilDue === 0) {
      return { text: 'Due today', color: '#f59e0b' };
    } else if (daysUntilDue <= 30) {
      return { text: `Due in ${daysUntilDue} days`, color: '#f59e0b' };
    } else {
      return { text: `Due ${dueDate.toLocaleDateString()}`, color: '#16a34a' };
    }
  };

  const renderMaintenanceTasks = () => {
    if (tasksLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      );
    }

    if (maintenancePlans.length === 0) {
      return (
        <View style={styles.emptyStateContainer}>
          <AlertTriangle size={32} color="#9ca3af" />
          <View style={styles.emptyStateTextContainer}>
            <Text style={styles.emptyStateTitle}>No Maintenance Tasks</Text>
            <Text style={styles.emptyStateDescription}>
              Configure your home systems and generate tasks to get started
            </Text>
          </View>
          <Pressable
            onPress={() => {
              setIsSetupOpen(true);
              setCurrentStep(0);
              // Initialize maintenance plan enabled to match configured systems
              // This way all configured systems are enabled by default
              setMaintenancePlanEnabled({
                heating_systems: systemData.heating_systems || [],
                cooling_systems: systemData.cooling_systems || [],
                appliances: systemData.appliances || [],
                other_systems: systemData.other_systems || [],
                upgrades: systemData.upgrades || [],
                outdoor_features: systemData.outdoor_features || []
              });
            }}
            style={styles.configureButton}
          >
            <Text style={styles.configureButtonText}>Configure Systems</Text>
          </Pressable>
        </View>
      );
    }

    const groupedPlans = maintenancePlans.reduce((acc, plan) => {
      // Try to determine system type from template, or default to 'maintenance'
      const systemType = plan.maintenance_templates?.system_type || 'maintenance';
      if (!acc[systemType]) {
        acc[systemType] = [];
      }
      acc[systemType].push(plan);
      return acc;
    }, {} as Record<string, MaintenancePlan[]>);

    return (
      <View style={styles.tasksContainer}>
        {Object.entries(groupedPlans).map(([type, plans]) => (
          <View key={type} style={styles.taskGroup}>
            <View style={styles.taskCardsGrid}>
              {plans.map(plan => {
                const template = plan.maintenance_templates;
                const priorityColors = getPriorityColor(template?.priority || 'normal');
                const statusInfo = getTaskStatus(plan);
                
                // Handle custom vs template tasks
                const taskName = plan.is_custom ? plan.custom_task_name : plan.maintenance_tasks?.name;
                const taskDescription = plan.is_custom ? plan.custom_description : plan.maintenance_tasks?.description;
                const estimatedDuration = plan.is_custom ? 60 : plan.maintenance_tasks?.estimated_duration_minutes; // Default 1 hour for custom
                
                return (
                  <View key={plan.id} style={styles.taskCard}>
                    <View style={styles.taskCardHeader}>
                      <View style={styles.taskCardHeaderLeft}>
                        <Text style={styles.taskCardTitle}>
                          {taskName}
                          {plan.is_custom && <Text style={styles.customBadgeText}> (Custom)</Text>}
                        </Text>
                        <Text style={styles.taskCardDescription}>{taskDescription}</Text>
                      </View>
                      <Pressable
                        onPress={() => handleOpenEditFrequencyModal(plan)}
                        style={styles.editFrequencyButton}
                      >
                        <Edit size={18} color="#6b7280" />
                      </Pressable>
                    </View>
                    
                    {/* Status and Due Date */}
                    <View style={styles.statusBanner}>
                      <Calendar size={16} color={statusInfo.color === '#dc2626' ? '#dc2626' : '#6b7280'} />
                      <Text style={[styles.statusText, statusInfo.color === '#dc2626' && { color: '#dc2626' }]}>
                        {statusInfo.text}
                      </Text>
                    </View>

                    {/* Last Completed Info */}
                    {plan.last_completed_date && (
                      <View style={styles.completionInfo}>
                        <CheckCircle2 size={16} color="#16a34a" />
                        <Text style={styles.completionText}>
                          Last done: {new Date(plan.last_completed_date).toLocaleDateString()}
                        </Text>
                        {plan.completed_by && (
                          <View style={styles.completedByBadge}>
                            {plan.completed_by === 'homeowner' ? (
                              <User size={14} color="#6b7280" />
                            ) : (
                              <Briefcase size={14} color="#6b7280" />
                            )}
                            <Text style={styles.completedByText}>
                              {plan.completed_by === 'homeowner' ? 'Self' : 'Professional'}
                            </Text>
                          </View>
                        )}
                      </View>
                    )}

                    <View style={styles.taskCardMeta}>
                      <View style={styles.taskCardMetaItem}>
                        <Clock size={16} color="#6b7280" />
                        <Text style={styles.taskCardMetaText}>
                          {formatDuration(estimatedDuration)}
                        </Text>
                      </View>
                      <View style={styles.taskCardMetaItem}>
                        <Calendar size={16} color="#6b7280" />
                        <Text style={styles.taskCardMetaText}>
                          Every {plan.custom_frequency_months || plan.maintenance_tasks?.frequency_months} month{(plan.custom_frequency_months || plan.maintenance_tasks?.frequency_months) !== 1 ? 's' : ''}
                        </Text>
                      </View>
                      {template?.professional_required && (
                        <View style={styles.taskCardMetaItem}>
                          <Wrench size={16} color="#6b7280" />
                          <Text style={styles.taskCardMetaText}>Professional required</Text>
                        </View>
                      )}
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.taskCardActions}>
                      <Pressable
                        onPress={() => handleOpenSnoozeModal(plan)}
                        style={styles.snoozeButton}
                      >
                        <Clock size={16} color="#6b7280" />
                        <Text style={styles.snoozeButtonText}>Snooze</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => handleOpenCompleteModal(plan)}
                        style={styles.markCompleteButton}
                      >
                        <CheckCircle2 size={16} color="#ffffff" />
                        <Text style={styles.markCompleteButtonText}>Mark Complete</Text>
                      </Pressable>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centeredLoadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  if (homes.length === 0) {
    return (
      <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={styles.header}>
          <Text style={styles.title}>Maintenance Plan</Text>
          <Text style={styles.subtitle}>Keep your home in perfect condition</Text>
        </View>

        <View style={styles.card}>
          <View style={styles.emptyStateContainer}>
            <AlertTriangle size={32} color="#9ca3af" />
            <View style={styles.emptyStateTextContainer}>
              <Text style={styles.emptyStateTitle}>No Homes Found</Text>
              <Text style={styles.emptyStateDescription}>Add a home to create a maintenance plan</Text>
            </View>
          </View>
        </View>
      </WebScrollView>
    );
  }

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View style={styles.headerTextContainer}>
          <Text style={styles.title}>Maintenance Plan</Text>
          <Text style={styles.subtitle}>Keep your home in perfect condition</Text>
        </View>
        <View style={styles.headerButtonsContainer}>
          <Pressable
            onPress={handleOpenCustomTaskModal}
            style={styles.primaryButton}
          >
            <Plus size={16} color="#ffffff" />
            <Text style={styles.primaryButtonText}>Add Task</Text>
          </Pressable>
          <Pressable
            onPress={() => {
              setIsSetupOpen(true);
              setCurrentStep(0);
              // Initialize maintenance plan enabled to match configured systems
              // This way all configured systems are enabled by default
              setMaintenancePlanEnabled({
                heating_systems: systemData.heating_systems || [],
                cooling_systems: systemData.cooling_systems || [],
                appliances: systemData.appliances || [],
                other_systems: systemData.other_systems || [],
                upgrades: systemData.upgrades || [],
                outdoor_features: systemData.outdoor_features || []
              });
            }}
            style={styles.ghostButton}
          >
            <Settings size={16} color="#6b7280" />
            <Text style={styles.ghostButtonText}>Configure Systems</Text>
          </Pressable>
        </View>
      </View>

      {error && (
        <View style={styles.errorAlert}>
          <AlertTriangle size={20} color="#dc2626" />
          <Text style={styles.errorAlertText}>{error}</Text>
        </View>
      )}

      <View style={styles.card}>
        {homes.length > 1 && (
        <View style={[styles.cardHeader, isCompactHomeSelector && styles.cardHeaderCompact]}>
          <Pressable
            onPress={() => setShowHomePicker(true)}
            style={[styles.homePickerButton, isCompactHomeSelector && styles.homePickerButtonCompact]}
          >
            <Text style={styles.homePickerButtonText} numberOfLines={1}>
              {homes.find(h => h.id === selectedHome)?.address || 'Select Home'}
            </Text>
          </Pressable>
        </View>
        )}

          {renderMaintenanceTasks()}
      </View>

      <BottomSheet
        isOpen={isSetupOpen}
        onClose={() => {
          setIsSetupOpen(false);
          setCurrentStep(0);
        }}
        title="Configure Home Systems"
        height="full"
      >
        <View style={styles.setupContainer}>
          {/* Progress Bar */}
          <View style={styles.progressBarContainer}>
            {[...Object.keys(systemOptions), 'review'].map((_, index) => (
              <View
                key={index}
                style={[
                  styles.progressBar,
                  index <= currentStep && styles.progressBarActive
                ]}
              />
            ))}
          </View>

          {/* Step Content - Scrollable */}
          <WebScrollView 
            style={styles.setupContent}
            contentContainerStyle={styles.setupContentContainer}
          >
            <View style={styles.setupContentWrapper}>
              {renderSetupStep()}
              
              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}
            </View>
          </WebScrollView>

          {/* Navigation Footer - Fixed at bottom */}
          <View style={styles.setupFooter}>
            <Pressable
              onPress={() => setCurrentStep(prev => prev - 1)}
              disabled={currentStep === 0}
              style={[styles.backButton, currentStep === 0 && styles.backButtonDisabled]}
            >
              <Text style={[styles.backButtonText, currentStep === 0 && styles.backButtonTextDisabled]}>
                Back
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                const totalSteps = Object.keys(systemOptions).length; // 6 steps + 1 review = 7 total
                if (currentStep === totalSteps) {
                  handleSubmit();
                } else {
                  setCurrentStep(prev => prev + 1);
                }
              }}
              disabled={submitting}
              style={[styles.nextButton, submitting && styles.nextButtonDisabled]}
            >
              {submitting ? (
                <>
                  <ActivityIndicator size="small" color="#ffffff" />
                  <Text style={styles.nextButtonText}>Saving...</Text>
                </>
              ) : currentStep === Object.keys(systemOptions).length ? (
                <>
                  <Text style={styles.nextButtonText}>Finish</Text>
                  <CheckCircle2 size={16} color="#ffffff" />
                </>
              ) : (
                <>
                  <Text style={styles.nextButtonText}>Next</Text>
                  <ArrowRight size={16} color="#ffffff" />
                </>
              )}
            </Pressable>
          </View>
        </View>
      </BottomSheet>

      <DropdownPicker
        visible={showHomePicker}
        onClose={() => setShowHomePicker(false)}
        options={homes.map(home => ({ 
          value: home.id, 
          label: `${home.address}, ${home.city}` 
        }))}
        selectedValue={selectedHome}
        onSelect={handleHomeChange}
      />

      {/* Custom Task Bottom Sheet */}
      <BottomSheet
        isOpen={showCustomTaskModal}
        onClose={handleCloseCustomTaskModal}
        title="Add Maintenance Task"
        height="auto"
      >
        <View style={styles.addTaskBottomSheetBody}>
          {/* Task Name */}
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Task Name *</Text>
            <TextInput
              style={styles.formInput}
              value={customTaskData.task_name}
              onChangeText={(text) => setCustomTaskData({ ...customTaskData, task_name: text })}
              placeholder="e.g., Clean koi pond filter"
              placeholderTextColor="#9ca3af"
            />
          </View>

          {/* Description */}
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Description</Text>
            <TextInput
              style={[styles.formInput, styles.textArea]}
              value={customTaskData.description}
              onChangeText={(text) => setCustomTaskData({ ...customTaskData, description: text })}
              placeholder="Add details about this maintenance task..."
              placeholderTextColor="#9ca3af"
              multiline
              numberOfLines={3}
            />
          </View>

          {/* Frequency */}
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>How often? *</Text>
            {Platform.OS === 'web' ? (
              <div className="inline-flex items-center space-x-2 bg-gray-50 px-3 py-2.5 rounded-md border border-gray-200">
                <span className="text-[15px] text-gray-600 whitespace-nowrap">Every</span>
                <input
                  type="number"
                  value={customTaskData.frequency_months}
                  onChange={(e) => setCustomTaskData({ ...customTaskData, frequency_months: e.target.value.replace(/[^0-9]/g, '') })}
                  min="1"
                  className="w-12 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-center text-lg font-bold bg-white"
                  style={{ display: 'inline-block' }}
                />
                <span className="text-[15px] text-gray-600 whitespace-nowrap">months</span>
              </div>
            ) : (
              <View style={styles.frequencyInputContainer}>
                <Text style={styles.frequencyPrefix}>Every</Text>
                <TextInput
                  style={styles.frequencyNumberInput}
                  value={customTaskData.frequency_months}
                  onChangeText={(text) => setCustomTaskData({ ...customTaskData, frequency_months: text.replace(/[^0-9]/g, '') })}
                  keyboardType="numeric"
                  placeholder="12"
                />
                <Text style={styles.frequencySuffix}>months</Text>
              </View>
            )}
          </View>

          {/* First Due Date */}
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>When is it due?</Text>
            {Platform.OS === 'web' ? (
              <input
                type="date"
                value={customTaskData.first_due_date}
                onChange={(e) => setCustomTaskData({ ...customTaskData, first_due_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
              />
            ) : (
              <TextInput
                style={styles.formInput}
                value={customTaskData.first_due_date}
                onChangeText={(text) => setCustomTaskData({ ...customTaskData, first_due_date: text })}
                placeholder="YYYY-MM-DD"
                placeholderTextColor="#9ca3af"
                keyboardType="numeric"
              />
            )}
          </View>

          {/* Link to Inventory Item */}
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Inventory Item (Optional)</Text>
            {inventoryItems.length > 0 ? (
              Platform.OS === 'web' ? (
                <select
                  value={customTaskData.inventory_item_id}
                  onChange={(e) => setCustomTaskData({ ...customTaskData, inventory_item_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <option value="">None</option>
                  {inventoryItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.category})
                    </option>
                  ))}
                </select>
              ) : (
                <TextInput
                  style={styles.formInput}
                  value={customTaskData.inventory_item_id}
                  placeholder="Select inventory item"
                  placeholderTextColor="#9ca3af"
                  editable={false}
                />
              )
            ) : (
              <Text style={styles.formHint}>No inventory items found for this home</Text>
            )}
          </View>

          {error && (
            <View style={styles.errorMessage}>
              <AlertTriangle size={16} color="#dc2626" />
              <Text style={styles.errorMessageText}>{error}</Text>
            </View>
          )}
        </View>

        {/* Footer Buttons */}
        <View style={styles.addTaskBottomSheetFooter}>
          <View style={styles.bsleft}>
            <Pressable
              onPress={handleCloseCustomTaskModal}
              style={styles.addTaskSecondaryButton}
            >
              <Text style={styles.secondaryButtonText}>Cancel</Text>
            </Pressable>
          </View>
          <View style={styles.bsright}>
            <Pressable
              onPress={handleCreateCustomTask}
              disabled={creatingCustomTask}
              style={styles.addTaskprimaryButton}
            >
              {creatingCustomTask ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={styles.addTaskprimaryButtonText}>Submit</Text>
              )}
            </Pressable>
          </View>
        </View>
      </BottomSheet>

      {/* Completion Modal */}
      {showCompleteModal && selectedPlan && (
        <Modal
          visible={showCompleteModal}
          transparent
          animationType="slide"
          onRequestClose={handleCloseCompleteModal}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={handleCloseCompleteModal} />
            <View style={styles.completeModalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Mark Task Complete</Text>
                <Pressable onPress={handleCloseCompleteModal}>
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.modalBody}>
                <View style={styles.completeFormContainer}>
                  <Text style={styles.completeTaskName}>{selectedPlan.maintenance_tasks.name}</Text>
                  <Text style={styles.completeTaskDescription}>
                    {selectedPlan.maintenance_tasks.description}
                  </Text>

                  {/* Completion Date */}
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>When was this completed?</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={completionData.completion_date}
                        onChange={(e) => setCompletionData({ ...completionData, completion_date: e.target.value })}
                        style={{
                          padding: '12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: '#d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#111827',
                          backgroundColor: '#ffffff',
                          width: '100%',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <TextInput
                        style={styles.dateInput}
                        value={completionData.completion_date}
                        onChangeText={(text) => setCompletionData({ ...completionData, completion_date: text })}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9ca3af"
                      />
                    )}
                  </View>

                  {/* Who performed */}
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>Who did this work?</Text>
                    <View style={styles.radioGroup}>
                      <Pressable
                        onPress={() => setCompletionData({ ...completionData, performed_by: 'homeowner' })}
                        style={[
                          styles.radioOption,
                          completionData.performed_by === 'homeowner' && styles.radioOptionSelected
                        ]}
                      >
                        <User size={20} color={completionData.performed_by === 'homeowner' ? '#ffffff' : '#6b7280'} />
                        <Text style={[
                          styles.radioText,
                          completionData.performed_by === 'homeowner' && styles.radioTextSelected
                        ]}>
                          I did it myself
                        </Text>
                      </Pressable>
                      <Pressable
                        onPress={() => setCompletionData({ ...completionData, performed_by: 'professional' })}
                        style={[
                          styles.radioOption,
                          completionData.performed_by === 'professional' && styles.radioOptionSelected
                        ]}
                      >
                        <Briefcase size={20} color={completionData.performed_by === 'professional' ? '#ffffff' : '#6b7280'} />
                        <Text style={[
                          styles.radioText,
                          completionData.performed_by === 'professional' && styles.radioTextSelected
                        ]}>
                          Professional service
                        </Text>
                      </Pressable>
                    </View>
                  </View>

                  {/* Notes */}
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>Notes (optional)</Text>
                    <TextInput
                      style={styles.notesInput}
                      value={completionData.notes}
                      onChangeText={(text) => setCompletionData({ ...completionData, notes: text })}
                      placeholder="Add any notes about this maintenance..."
                      placeholderTextColor="#9ca3af"
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                    />
                  </View>
                </View>
              </WebScrollView>

              <View style={styles.modalFooter}>
                <Pressable
                  onPress={handleCloseCompleteModal}
                  style={styles.cancelButton}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={handleCompleteTask}
                  disabled={completing}
                  style={[styles.submitButton, completing && styles.submitButtonDisabled]}
                >
                  {completing ? (
                    <>
                      <ActivityIndicator size="small" color="#ffffff" />
                      <Text style={styles.submitButtonText}>Saving...</Text>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} color="#ffffff" />
                      <Text style={styles.submitButtonText}>Mark Complete</Text>
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* Edit Frequency Modal */}
      {showEditFrequencyModal && editingPlan && (
        <BottomSheet
          isOpen={showEditFrequencyModal}
          onClose={handleCloseEditFrequencyModal}
          title="Edit Frequency"
          height="auto"
        >
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>How often should this task be performed? *</Text>
            {Platform.OS === 'web' ? (
              <div className="inline-flex items-center space-x-2 bg-gray-50 px-3 py-2.5 rounded-md border border-gray-200">
                <span className="text-[15px] text-gray-600 whitespace-nowrap">Every</span>
                <input
                  type="number"
                  value={editFrequencyData.frequency_months}
                  onChange={(e) => setEditFrequencyData({ ...editFrequencyData, frequency_months: e.target.value.replace(/[^0-9]/g, '') })}
                  min="1"
                  className="w-12 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-center text-lg font-bold bg-white"
                  style={{ display: 'inline-block' }}
                />
                <span className="text-[15px] text-gray-600 whitespace-nowrap">months</span>
              </div>
            ) : (
              <View style={styles.frequencyInputContainer}>
                <Text style={styles.frequencyPrefix}>Every</Text>
                <TextInput
                  style={styles.frequencyNumberInput}
                  value={editFrequencyData.frequency_months}
                  onChangeText={(text) => setEditFrequencyData({ ...editFrequencyData, frequency_months: text.replace(/[^0-9]/g, '') })}
                  keyboardType="numeric"
                  placeholder="12"
                />
                <Text style={styles.frequencySuffix}>months</Text>
              </View>
            )}
          </View>

          {error && (
            <View style={styles.errorMessage}>
              <AlertTriangle size={16} color="#dc2626" />
              <Text style={styles.errorMessageText}>{error}</Text>
            </View>
          )}

          {/* Delete Button Section */}
          <View style={styles.editModalDeleteSection}>
            <View style={styles.editModalDeleteDivider} />
            <Pressable
              onPress={() => {
                handleCloseEditFrequencyModal();
                handleOpenDeleteModal(editingPlan);
              }}
              style={styles.editModalDeleteButton}
            >
              <Trash2 size={16} color="#dc2626" />
              <Text style={styles.editModalDeleteButtonText}>Delete Task</Text>
            </Pressable>
          </View>

          {/* Footer Buttons */}
          <View style={styles.addTaskBottomSheetFooter}>
            <View style={styles.bsleft}>
              <Pressable
                onPress={handleCloseEditFrequencyModal}
                style={styles.addTaskSecondaryButton}
              >
                <Text style={styles.secondaryButtonText}>Cancel</Text>
              </Pressable>
            </View>
            <View style={styles.bsright}>
              <Pressable
                onPress={handleUpdateFrequency}
                disabled={updatingFrequency}
                style={styles.addTaskprimaryButton}
              >
                {updatingFrequency ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.addTaskprimaryButtonText}>Save</Text>
                )}
              </Pressable>
            </View>
          </View>
        </BottomSheet>
      )}

      {/* Snooze Modal */}
      {showSnoozeModal && snoozingPlan && (
        <BottomSheet
          isOpen={showSnoozeModal}
          onClose={handleCloseSnoozeModal}
          title="Snooze Task"
          height="auto"
        >
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>How long would you like to snooze this task? *</Text>
            <Text style={styles.formHint}>
              The next due date will be pushed forward by the selected number of months.
            </Text>
            {Platform.OS === 'web' ? (
              <div className="inline-flex items-center space-x-2 bg-gray-50 px-3 py-2.5 rounded-md border border-gray-200 mt-2">
                <span className="text-[15px] text-gray-600 whitespace-nowrap">Snooze for</span>
                <input
                  type="number"
                  value={snoozeData.months}
                  onChange={(e) => setSnoozeData({ ...snoozeData, months: e.target.value.replace(/[^0-9]/g, '') })}
                  min="1"
                  className="w-12 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-center text-lg font-bold bg-white"
                  style={{ display: 'inline-block' }}
                />
                <span className="text-[15px] text-gray-600 whitespace-nowrap">months</span>
              </div>
            ) : (
              <View style={styles.frequencyInputContainer}>
                <Text style={styles.frequencyPrefix}>Snooze for</Text>
                <TextInput
                  style={styles.frequencyNumberInput}
                  value={snoozeData.months}
                  onChangeText={(text) => setSnoozeData({ ...snoozeData, months: text.replace(/[^0-9]/g, '') })}
                  keyboardType="numeric"
                  placeholder="3"
                />
                <Text style={styles.frequencySuffix}>months</Text>
              </View>
            )}
          </View>

          {snoozingPlan.next_due_date && (
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Current due date:</Text>
              <Text style={styles.formHint}>
                {formatDateForDisplay(snoozingPlan.next_due_date)}
              </Text>
              <Text style={[styles.formLabel, { marginTop: 8 }]}>
                New due date will be:
              </Text>
              <Text style={styles.formHint}>
                {(() => {
                  const currentDueDate = new Date(snoozingPlan.next_due_date);
                  const snoozeMonths = parseInt(snoozeData.months) || 0;
                  const newDueDate = new Date(currentDueDate);
                  newDueDate.setMonth(newDueDate.getMonth() + snoozeMonths);
                  return newDueDate.toLocaleDateString();
                })()}
              </Text>
            </View>
          )}

          {error && (
            <View style={styles.errorMessage}>
              <AlertTriangle size={16} color="#dc2626" />
              <Text style={styles.errorMessageText}>{error}</Text>
            </View>
          )}

          {/* Footer Buttons */}
          <View style={styles.addTaskBottomSheetFooter}>
            <View style={styles.bsleft}>
              <Pressable
                onPress={handleCloseSnoozeModal}
                style={styles.addTaskSecondaryButton}
              >
                <Text style={styles.secondaryButtonText}>Cancel</Text>
              </Pressable>
            </View>
            <View style={styles.bsright}>
              <Pressable
                onPress={handleSnoozeTask}
                disabled={snoozing}
                style={styles.addTaskprimaryButton}
              >
                {snoozing ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.addTaskprimaryButtonText}>Snooze</Text>
                )}
              </Pressable>
            </View>
          </View>
        </BottomSheet>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingPlan && (
        <BottomSheet
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          title="Delete Maintenance Task"
          height="auto"
        >
          <View style={styles.formGroup}>
            <View style={styles.deleteWarningContainer}>
              <AlertTriangle size={24} color="#dc2626" />
              <Text style={styles.deleteWarningTitle}>Permanent Deletion</Text>
            </View>
            <Text style={styles.deleteWarningText}>
              Are you sure you want to delete "{deletingPlan.is_custom ? deletingPlan.custom_task_name : deletingPlan.maintenance_tasks?.name}"?
            </Text>
            <Text style={styles.deleteWarningDescription}>
              This will permanently remove this task from your maintenance plan and delete all future instances of it.
            </Text>
            <View style={styles.deleteSnoozeHint}>
              <Clock size={16} color="#6b7280" />
              <Text style={styles.deleteSnoozeHintText}>
                Tip: If you just want to skip this instance, use the Snooze button instead.
              </Text>
            </View>
          </View>

          {error && (
            <View style={styles.errorMessage}>
              <AlertTriangle size={16} color="#dc2626" />
              <Text style={styles.errorMessageText}>{error}</Text>
            </View>
          )}

          {/* Footer Buttons */}
          <View style={styles.addTaskBottomSheetFooter}>
            <View style={styles.bsleft}>
              <Pressable
                onPress={handleCloseDeleteModal}
                style={styles.addTaskSecondaryButton}
              >
                <Text style={styles.secondaryButtonText}>Cancel</Text>
              </Pressable>
            </View>
            <View style={styles.bsright}>
              <Pressable
                onPress={handleDeleteTask}
                disabled={deleting}
                style={[styles.deleteConfirmButton, deleting && styles.deleteConfirmButtonDisabled]}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <>
                    <Trash2 size={16} color="#ffffff" />
                    <Text style={styles.deleteConfirmButtonText}>Delete</Text>
                  </>
                )}
              </Pressable>
            </View>
          </View>
        </BottomSheet>
      )}
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentContainer: {
    padding: 16,
    gap: 32,
  },
  header: {
    gap: 16,
  },
  headerTextContainer: {
    gap: 4,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
  },
  headerButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '400',
  },
  addTaskprimaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addTaskprimaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '400',
  },
  ghostButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  ghostButtonText: {
    color: '#6b7280',
    fontSize: 14,
    fontWeight: '400',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  secondaryButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '400',
  },
  addTaskSecondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 1,
    flex: 1,
    gap: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  successAlert: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: '#f0fdf4',
    borderRadius: 8,
  },
  successAlertText: {
    color: '#16a34a',
    fontSize: 14,
  },
  errorAlert: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  errorAlertText: {
    color: '#dc2626',
    fontSize: 14,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  cardHeaderCompact: {
    alignSelf: 'stretch',
    width: '100%',
    paddingHorizontal: 0,
  },
  homePickerButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    minWidth: 150,
    maxWidth: 250,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homePickerButtonCompact: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
  },
  homePickerButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centeredLoadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
  },
  emptyStateContainer: {
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  emptyStateTextContainer: {
    gap: 8,
    alignItems: 'center',
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  configureButton: {
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#000000',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  configureButtonText: {
    fontSize: 14,
    color: '#000000',
  },
  tasksContainer: {
    paddingTop: 24,
    paddingBottom: 24,
    gap: 24,
  },
  taskGroup: {
    gap: 16,
  },
  taskGroupTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    textTransform: 'capitalize',
  },
  taskCardsGrid: {
    gap: 16,
    ...(Platform.OS === 'web' && {
      display: 'grid' as any,
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' as any,
    }),
  },
  taskCard: {
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    gap: 12,
  },
  taskCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  taskCardHeaderLeft: {
    flex: 1,
    gap: 4,
  },
  taskCardTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  taskCardDescription: {
    fontSize: 14,
    color: '#6b7280',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  priorityBadgeText: {
    fontSize: 14,
  },
  taskCardMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  taskCardMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  taskCardMetaText: {
    fontSize: 14,
    color: '#6b7280',
  },
  statusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  completionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  completionText: {
    fontSize: 14,
    color: '#6b7280',
  },
  completedByBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
  },
  completedByText: {
    fontSize: 12,
    color: '#6b7280',
  },
  taskCardActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  snoozeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flex: 1,
    paddingVertical: 10,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  snoozeButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  markCompleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    flex: 1,
    paddingVertical: 10,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  markCompleteButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    maxWidth: 500,
    width: '100%',
    maxHeight: '90%',
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 32,
    backgroundColor: '#ffffff',
  },
  progressBar: {
    height: 8,
    width: 64,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
  },
  progressBarActive: {
    backgroundColor: '#000000',
  },
  modalBody: {
    padding: 24,
    paddingTop: 20,
    ...(Platform.OS === 'web' && {
      overflowY: 'auto' as any,
      maxHeight: 'calc(90vh - 180px)' as any,
    }),
  },
  setupContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Important for flex scrolling
  },
  setupContent: {
    flex: 1,
    minHeight: 0, // Important for flex scrolling
  },
  setupContentContainer: {
    paddingTop: 8,
    paddingBottom: 16,
  },
  setupContentWrapper: {
    paddingHorizontal: 32,
  },
  setupFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingTop: 20,
    paddingBottom: Platform.OS === 'web' ? 20 : 40,
    paddingHorizontal: 32,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    backgroundColor: '#ffffff',
    flexShrink: 0, // Prevent footer from shrinking
  },
  errorContainer: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  setupStepContainer: {
    gap: 24,
  },
  reviewScrollView: {
    maxHeight: Platform.OS === 'web' ? 500 : 400,
    flex: 1,
  },
  reviewScrollContent: {
    paddingBottom: 16,
  },
  reviewCategory: {
    marginBottom: 24,
    gap: 12,
  },
  reviewCategoryTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  reviewSystemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    marginBottom: 8,
  },
  reviewSystemLabel: {
    fontSize: 16,
    color: '#374151',
    flex: 1,
  },
  toggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#d1d5db',
    padding: 2,
    justifyContent: 'center',
  },
  toggleEnabled: {
    backgroundColor: '#16a34a',
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#ffffff',
    alignSelf: 'flex-start',
  },
  toggleThumbEnabled: {
    alignSelf: 'flex-end',
  },
  setupStepHeader: {
    gap: 8,
  },
  setupStepTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  setupStepDescription: {
    fontSize: 16,
    color: '#6b7280',
  },
  systemOptionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  systemOption: {
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    width: 'calc(50% - 6px)', // 2 items per row accounting for gap
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxSizing: 'border-box',
    }),
    ...(Platform.OS !== 'web' && {
      flexBasis: '48%',
      flexGrow: 0,
      flexShrink: 0,
    }),
  },
  systemOptionSelected: {
    backgroundColor: '#000000',
  },
  systemOptionText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  systemOptionTextSelected: {
    color: '#ffffff',
  },
  errorText: {
    marginTop: 16,
    fontSize: 14,
    color: '#dc2626',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    backgroundColor: '#f9fafb',
  },
  backButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  backButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed' as any,
    }),
  },
  backButtonText: {
    fontSize: 14,
    color: '#6b7280',
  },
  backButtonTextDisabled: {
    color: '#d1d5db',
  },
  nextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  nextButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed' as any,
    }),
  },
  nextButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  completeModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    maxWidth: 500,
    width: '100%',
    maxHeight: '80%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  completeFormContainer: {
    gap: 20,
  },
  completeTaskName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  completeTaskDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8,
  },
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  formInput: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  dateInput: {
    padding: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  radioGroup: {
    gap: 12,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  radioOptionSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  radioText: {
    fontSize: 14,
    color: '#111827',
  },
  radioTextSelected: {
    color: '#ffffff',
  },
  notesInput: {
    padding: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 100,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#6b7280',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  submitButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed' as any,
    }),
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  editFrequencyButton: {
    padding: 4,
    alignSelf: 'flex-start',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  editModalDeleteSection: {
    marginTop: 8,
    marginBottom: 16,
  },
  editModalDeleteDivider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginBottom: 16,
  },
  editModalDeleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  editModalDeleteButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#dc2626',
  },
  deleteWarningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  deleteWarningTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc2626',
  },
  deleteWarningText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 12,
  },
  deleteWarningDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 16,
  },
  deleteSnoozeHint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  deleteSnoozeHintText: {
    flex: 1,
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 18,
  },
  deleteConfirmButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#dc2626',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  deleteConfirmButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed' as any,
    }),
  },
  deleteConfirmButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  frequencyInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    backgroundColor: '#f9fafb',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  frequencyPrefix: {
    fontSize: 15,
    color: '#6b7280',
  },
  frequencyNumberInput: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 18,
    width: 48,
    textAlign: 'center',
    color: '#111827',
    backgroundColor: '#ffffff',
    fontWeight: '700',
  },
  frequencySuffix: {
    fontSize: 15,
    color: '#6b7280',
  },
  textArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  formHint: {
    fontSize: 14,
    color: '#6b7280',
  },
  errorMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  errorMessageText: {
    fontSize: 14,
    color: '#dc2626',
  },
  addTaskBottomSheetBody: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 20,
    paddingRight: 20,
  },
  addTaskBottomSheetFooter: {
    flexDirection: 'row',
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 20,
    paddingRight: 20,
    justifyContent: 'space-around',
    flex:1,
    ...(Platform.OS === 'web' && {
      boxSizing: 'border-box' as any,
    }),
  },
  bsleft: {
    flex: 1,
    paddingRight: 4,
  },
  bsright: {
    flex: 1,
    paddingLeft: 4,
  },
  customBadgeText: {
    fontSize: 12,
    color: '#8b5cf6',
    fontWeight: '500',
  },
});

const pickerStyles = StyleSheet.create({
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '60%',
    ...Platform.select({
      web: {
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 5,
      },
    }),
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerOptions: {
    flex: 1,
  },
  pickerOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerOptionSelected: {
    backgroundColor: '#f9fafb',
  },
  pickerOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerOptionTextSelected: {
    fontWeight: '600',
    color: '#000000',
  },
  frequencyInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    backgroundColor: '#f9fafb',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  frequencyPrefix: {
    fontSize: 15,
    color: '#6b7280',
  },
  frequencyNumberInput: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 18,
    width: 48,
    textAlign: 'center',
    color: '#111827',
    backgroundColor: '#ffffff',
    fontWeight: '700',
  },
  frequencySuffix: {
    fontSize: 15,
    color: '#6b7280',
  },
  textArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  formHint: {
    fontSize: 14,
    color: '#6b7280',
  },
  errorMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  errorMessageText: {
    fontSize: 14,
    color: '#dc2626',
  },
  customBadgeText: {
    fontSize: 12,
    color: '#8b5cf6',
    fontWeight: '500',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  modalCloseButton: {
    padding: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
});

export default MaintenancePlan;
